import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  Grid
} from '@mui/material';
import { io } from 'socket.io-client';
import { bindGameResync } from '../../../../utils/bindGameResync';
import {
  ArrowBack as BackIcon,
  Bolt as FlashIcon,
  EmojiEvents as TrophyIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  MusicNote as MusicIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PracticePointsLeaderboard from './PracticePointsLeaderboard';

// Styled components
const WaitingContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#121210',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#0b1120',
  border: '1px solid rgba(148, 163, 184, 0.25)',
  borderRadius: 16,
  marginBottom: 24,
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.6)',
}));

const EqualHeightCardContent = styled(CardContent)(({ theme }) => ({
  textAlign: 'center',
  minHeight: 160,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

const LeaderboardCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#020617',
  border: '1px solid rgba(148, 163, 184, 0.3)',
  borderRadius: 16,
  marginBottom: 24,
}));

const FlashQuizWaiting = ({
  session,
  participant,
  onBack,
  onQuestionReceived,
  lastPushedAt: parentLastPushedAt,
  onSessionEnd,
  quizletCompletionData,
  stopNotice,
  onStopNoticeConsumed,
  practiceMode = false,
  practiceLeaderboard,
  practiceParticipantCount,
  practicePushedQuizlets,
  practiceQuizletStats,
  practiceNextDeliveryTime,
}) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [pushedQuizlets, setPushedQuizlets] = useState([]);
  const [participantCount, setParticipantCount] = useState(0);
  const [nextDeliveryTime, setNextDeliveryTime] = useState(() => practiceNextDeliveryTime || stopNotice?.deliveryTime || null);
  const [countdown, setCountdown] = useState(null);
  const [questionStoppedByAdmin, setQuestionStoppedByAdmin] = useState(() => !!stopNotice?.timestamp);
  const [completionData, setCompletionData] = useState(() => quizletCompletionData ? ({
    ...quizletCompletionData,
    rankPending: typeof quizletCompletionData.quizletRank !== 'number'
  }) : null);
  const [quizletStats, setQuizletStats] = useState({}); // Map of quizlet_id -> stats
  const activeQuizletIdRef = useRef(null);

  // Fetch leaderboard data
  const fetchLeaderboard = useCallback(async () => {
    if (!session?.session_id) return;

    try {
      setLoadingLeaderboard(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/live-quiz/sessions/${session.session_id}/leaderboard`);

      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard');
      }

      const data = await response.json();
      setLeaderboard(data.leaderboard || []);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    } finally {
      setLoadingLeaderboard(false);
    }
  }, [session?.session_id]);

  // Update participant count
  const updateParticipantCount = useCallback(async () => {
    if (!session?.session_id) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${session.session_id}/participants`);

      if (response.ok) {
        const data = await response.json();
        setParticipantCount(data.participants?.length || 0);
      }
    } catch (err) {
      console.error('Error fetching participant count:', err);
    }
  }, [session?.session_id]);

  // Use ref to store quizletStats to avoid dependency issues
  const quizletStatsRef = useRef({});
  useEffect(() => {
    quizletStatsRef.current = quizletStats;
  }, [quizletStats]);

  // Safely derive time taken for a quizlet – prefer backend `time_taken`,
  // otherwise fall back to difference between quizlet start (encoded in quizlet_id)
  // and `completed_at` if both are available.
  const getQuizletTimeTakenSeconds = useCallback((stat) => {
    if (!stat) return null;
    if (stat.time_taken !== undefined && stat.time_taken !== null) {
      const n = Number(stat.time_taken);
      return Number.isFinite(n) ? n : null;
    }

    try {
      const quizletId = String(stat.quizlet_id || '');
      const parts = quizletId.split('_');
      if (parts.length < 2 || !stat.completed_at) return null;

      const pushedIso = parts.slice(1).join('_');
      const startMs = new Date(pushedIso).getTime();
      const endMs = new Date(stat.completed_at).getTime();
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;

      const diffSec = Math.round((endMs - startMs) / 1000);
      return diffSec >= 0 ? diffSec : null;
    } catch {
      return null;
    }
  }, []);

  // Fetch quizlet stats for participant
  const fetchQuizletStats = useCallback(async (sessionId, participantId) => {
    if (!sessionId || !participantId) return;
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const res = await fetch(`${baseUrl}/api/live-quiz/sessions/${sessionId}/participants/${participantId}/quizlet-stats`);
      if (!res.ok) {
        console.warn('Failed to fetch quizlet stats');
        return;
      }
      const data = await res.json();
      if (data.success && Array.isArray(data.stats)) {
        // Convert array to map for easy lookup
        const statsMap = {};
        data.stats.forEach(stat => {
          statsMap[stat.quizlet_id] = stat;
        });
        setQuizletStats(statsMap);
      }
    } catch (e) {
      console.error('FlashQuizWaiting: Failed to fetch quizlet stats:', e);
    }
  }, []);

  // Fetch pushed quizlets
  const fetchPushedQuizlets = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${baseUrl}/api/live-quiz/flash/sessions/${sessionId}/pushed-quizlets`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && Array.isArray(data.items)) {
        const parsedRapid = (() => {
          const raw = session?.rapid_config;
          try { return typeof raw === 'string' ? JSON.parse(raw) : (raw || {}); } catch { return {}; }
        })();
        const songTimer = typeof parsedRapid.song_timer_seconds === 'number' ? parsedRapid.song_timer_seconds : 60;
        const items = data.items.map((it, idx) => {
          const deliveryMs = it.delivery_time ? new Date(it.delivery_time).getTime() : null;
          const expireAt = deliveryMs ? (deliveryMs + songTimer * 1000) : null;
          const now = Date.now();
          const isExpired = expireAt ? now > expireAt : false;
          const quizletId = it.id || `${it.song?.songs_master_id || 'song'}_${it.pushed_at}`;
          // Check if user has completed this quizlet (has stats) - use ref to avoid dependency
          const userHasCompleted = quizletStatsRef.current[quizletId] !== undefined;

          return {
            id: quizletId,
            song: it.song || {},
            pushed_at: it.pushed_at,
            delivery_time: it.delivery_time,
            expire_at: expireAt,
            status: userHasCompleted ? 'completed' : (idx === 0 && !isExpired ? 'running' : 'expired'),
            userPlayed: userHasCompleted
          };
        });
        const runningItem = items.find(it => it.status === 'running') || items[0];
        activeQuizletIdRef.current = runningItem?.id || null;
        // Merge with existing to preserve userPlayed flags
        setPushedQuizlets(prev => {
          const prevById = new Map(prev.map(p => [p.id, p]));
          return items.map(item => {
            const existing = prevById.get(item.id);
            // Use ref for latest stats when merging
            const userHasCompleted = quizletStatsRef.current[item.id] !== undefined;
            return existing ? { ...item, userPlayed: userHasCompleted || !!existing.userPlayed } : item;
          });
        });
      }
    } catch (e) {
      console.error('FlashQuizWaiting: Failed to fetch pushed quizlets:', e);
    }
  }, [session?.rapid_config]);

  // Use refs for functions to avoid dependency issues
  const fetchLeaderboardRef = useRef(fetchLeaderboard);
  const updateParticipantCountRef = useRef(updateParticipantCount);
  const fetchPushedQuizletsRef = useRef(fetchPushedQuizlets);
  const fetchQuizletStatsRef = useRef(fetchQuizletStats);
  const onQuestionReceivedRef = useRef(onQuestionReceived);
  const onSessionEndRef = useRef(onSessionEnd);

  useEffect(() => {
    if (!quizletCompletionData) {
      setCompletionData(null);
      return;
    }
    setCompletionData({
      ...quizletCompletionData,
      rankPending: typeof quizletCompletionData.quizletRank !== 'number'
    });
  }, [quizletCompletionData]);

  useEffect(() => {
    fetchLeaderboardRef.current = fetchLeaderboard;
    updateParticipantCountRef.current = updateParticipantCount;
    fetchPushedQuizletsRef.current = fetchPushedQuizlets;
    fetchQuizletStatsRef.current = fetchQuizletStats;
    onQuestionReceivedRef.current = onQuestionReceived;
    onSessionEndRef.current = onSessionEnd;
  }, [fetchLeaderboard, updateParticipantCount, fetchPushedQuizlets, fetchQuizletStats, onQuestionReceived, onSessionEnd]);

  // WebSocket listeners for real-time updates
  useEffect(() => {
    if (practiceMode) {
      if (Array.isArray(practiceLeaderboard)) setLeaderboard(practiceLeaderboard);
      if (typeof practiceParticipantCount === 'number') setParticipantCount(practiceParticipantCount);
      if (Array.isArray(practicePushedQuizlets)) setPushedQuizlets(practicePushedQuizlets);
      if (practiceQuizletStats && typeof practiceQuizletStats === 'object') setQuizletStats(practiceQuizletStats);
      return undefined;
    }

    if (!session?.session_id) return;

    // Initial fetch for immediate display (only once)
    fetchLeaderboardRef.current();
    updateParticipantCountRef.current();
    fetchPushedQuizletsRef.current(session.session_id);
    if (participant?.participant_id) {
      fetchQuizletStatsRef.current(session.session_id, participant.participant_id);
    }

    let socket;
    let unbindResync = () => {};
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      socket = io(baseUrl, { transports: ['websocket'] });

      socket.on('connect', () => {
        socket.emit('flash:join', { sessionId: session.session_id });
        fetchPushedQuizletsRef.current(session.session_id);
        fetchLeaderboardRef.current();
        if (participant?.participant_id) {
          fetchQuizletStatsRef.current(session.session_id, participant.participant_id);
        }
      });
      unbindResync = bindGameResync(() => {
        if (socket.connected) socket.emit('flash:join', { sessionId: session.session_id });
        fetchPushedQuizletsRef.current(session.session_id);
        fetchLeaderboardRef.current();
        if (participant?.participant_id) {
          fetchQuizletStatsRef.current(session.session_id, participant.participant_id);
        }
      });

      socket.on('flash:question_pushed', (payload) => {
        if (payload && payload.session_id === session.session_id) {
          // console.log('[FlashQuizWaiting] Question pushed:', payload);
          // Scroll to top when question is pushed
          window.scrollTo({ top: 0, behavior: 'smooth' });
          // Reset stopped state when new question is pushed
          setQuestionStoppedByAdmin(false);
          if (payload?.current_song) {
            const derivedId = `${payload.current_song?.songs_master_id || 'song'}_${payload.pushed_at || payload.delivery_time || Date.now()}`;
            activeQuizletIdRef.current = derivedId;
          }
          // Set countdown timer based on delivery_time
          if (payload.delivery_time) {
            setNextDeliveryTime(payload.delivery_time);
          }
          // Refresh pushed quizlets when a new question is pushed
          fetchPushedQuizletsRef.current(session.session_id);
          // Refresh quizlet stats to get final ranks for previous quizlet
          if (participant?.participant_id) {
            // Wait a bit for rank calculation to complete
            setTimeout(() => {
              fetchQuizletStatsRef.current(session.session_id, participant.participant_id);
            }, 1000);
          }
          onQuestionReceivedRef.current(payload);
        }
      });

      socket.on('flash:session_ended', (payload) => {
        if (payload && payload.session_id === session.session_id) {
          // console.log('[FlashQuizWaiting] Session ended event received');
          if (onSessionEndRef.current) {
            onSessionEndRef.current();
          }
        }
      });

      socket.on('flash:question_stopped', (payload) => {
        if (payload && payload.session_id === session.session_id) {
          // console.log('[FlashQuizWaiting] Question stopped event received - setting questionStoppedByAdmin to TRUE');
          // Set stopped state to show message
          setQuestionStoppedByAdmin(true);
          activeQuizletIdRef.current = null;
          setPushedQuizlets(prev => {
            if (!prev.length) return prev;
            const runningId = prev[0]?.id;
            return prev.filter((quizlet, idx) => {
              if (idx === 0) return false;
              if (runningId) return quizlet.id !== runningId;
              return idx !== 0;
            });
          });
          // Clear countdown
          setNextDeliveryTime(null);
          setCountdown(null);
          // Refresh quizlet stats after stopping
          if (participant?.participant_id) {
            setTimeout(() => {
              fetchQuizletStatsRef.current(session.session_id, participant.participant_id);
            }, 1000);
          }
        }
      });

      socket.on('flash:leaderboard', (payload) => {
        if (payload && payload.session_id === session.session_id) {
          setLeaderboard(payload.leaderboard || []);
        }
      });

      socket.on('flash:participants', (payload) => {
        if (payload && payload.session_id === session.session_id) {
          setParticipantCount(payload.participants?.length || 0);
        }
      });

      socket.on('flash:quizlet_rank_updated', (payload) => {
        if (!payload || Number(payload.session_id) !== Number(session.session_id)) return;
        if (participant?.participant_id) {
          fetchQuizletStatsRef.current(session.session_id, participant.participant_id);
        }
      });

    } catch (error) {
      console.warn('Socket connection failed, falling back to polling:', error);
      // Fallback to polling if socket fails (but with longer intervals)
      const lbTimer = setInterval(() => fetchLeaderboardRef.current(), 10000); // 10 seconds instead of 3
      const pTimer = setInterval(() => updateParticipantCountRef.current(), 15000); // 15 seconds instead of 5

      return () => {
        clearInterval(lbTimer);
        clearInterval(pTimer);
      };
    }

    return () => {
      try {
        unbindResync();
        if (socket) {
          socket.emit('flash:leave', { sessionId: session.session_id });
          socket.disconnect();
        }
      } catch (error) {
        console.warn('Socket cleanup error:', error);
      }
    };
  }, [session?.session_id, participant?.participant_id, practiceMode, practiceLeaderboard, practiceParticipantCount, practicePushedQuizlets, practiceQuizletStats]);

  useEffect(() => {
    if (practiceMode && practiceNextDeliveryTime) {
      setNextDeliveryTime(practiceNextDeliveryTime);
    }
  }, [practiceMode, practiceNextDeliveryTime]);

  // React to stop notices - simple: just set the flag to true
  useEffect(() => {
    if (stopNotice?.timestamp) {
      setQuestionStoppedByAdmin(true);
      if (stopNotice.deliveryTime) {
        setNextDeliveryTime(stopNotice.deliveryTime);
        setCountdown(null);
      }
      if (typeof onStopNoticeConsumed === 'function') {
        onStopNoticeConsumed();
      }
    }
  }, [stopNotice, onStopNoticeConsumed]);

  useEffect(() => {
    if (quizletCompletionData) {
      const completedId = quizletCompletionData.quizletId || activeQuizletIdRef.current;
      setPushedQuizlets(prev => {
        if (!prev.length) return prev;
        const fallbackId = prev[0]?.id;
        const targetId = completedId && prev.some(q => q.id === completedId)
          ? completedId
          : fallbackId;
        return prev.map(q =>
          targetId && q.id === targetId ? { ...q, userPlayed: true } : q
        );
      });
      // Refresh quizlet stats after completion
      if (session?.session_id && participant?.participant_id) {
        // Wait a bit for rank calculation to complete
        setTimeout(() => {
          fetchQuizletStatsRef.current(session.session_id, participant.participant_id);
        }, 1000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizletCompletionData]);

  useEffect(() => {
    if (!completionData?.quizletId) return;
    const latestStats = quizletStats[completionData.quizletId];
    if (!latestStats) return;

    const mergedRank = latestStats.quizlet_rank ?? null;
    const mergedQuestions = latestStats.questions_count ?? completionData.totalQuestions;
    const mergedPoints = latestStats.total_points ?? completionData.pointsScored;
    const rankPending = typeof mergedRank !== 'number';

    if (
      completionData.quizletRank !== mergedRank ||
      completionData.totalQuestions !== mergedQuestions ||
      completionData.pointsScored !== mergedPoints ||
      completionData.rankPending !== rankPending
    ) {
      setCompletionData(prev => prev ? ({
        ...prev,
        totalQuestions: mergedQuestions,
        pointsScored: mergedPoints,
        quizletRank: mergedRank,
        rankPending
      }) : prev);
    }
  }, [quizletStats, completionData]);

  // Merge latest quizlet stats into completion data so rank updates without refresh

  // Countdown timer effect
  useEffect(() => {
    if (!nextDeliveryTime) {
      setCountdown(null);
      return;
    }

    const tick = () => {
      const now = Date.now();
      const deliveryMs = new Date(nextDeliveryTime).getTime();
      const remaining = Math.max(0, Math.ceil((deliveryMs - now) / 1000));
      setCountdown(remaining);

      if (remaining <= 0) {
        setNextDeliveryTime(null);
        setCountdown(null);
      }
    };

    tick(); // Initial tick
    const intervalId = setInterval(tick, 250); // Update every 250ms for smooth countdown

    return () => clearInterval(intervalId);
  }, [nextDeliveryTime]);

  // Get user's position in leaderboard
  const getUserPosition = () => {
    if (!participant?.participant_id) return null;

    const userIndex = leaderboard.findIndex(entry =>
      entry.participant_id === participant.participant_id
    );

    return userIndex >= 0 ? userIndex + 1 : null;
  };

  const userPosition = getUserPosition();
  // Determine display state and messages
  const getDisplayState = () => {
    if (questionStoppedByAdmin) {
      return {
        type: 'stopped',
        title: 'Quizlet Stopped',
        message: 'The current quizlet has been stopped by the admin.\nYour scores won’t be counted for this quizlet, and your leaderboard position will remain unchanged.\nHang tight — a new quizlet will begin soon! 🎯',
        iconColor: '#f44336'
      };
    }

    if (completionData) {
      const latestStats = completionData.quizletId
        ? quizletStats[completionData.quizletId]
        : null;

      const mergedTotalQuestions = latestStats?.questions_count ?? completionData.totalQuestions;
      const mergedPoints = latestStats?.total_points ?? completionData.pointsScored;
      const timedOut = !!completionData.timedOut;
      const derivedOverallRank = typeof completionData.rank === 'number'
        ? completionData.rank
        : (typeof completionData.rank === 'string' ? parseInt(completionData.rank, 10) : null);
      const mergedRank = latestStats?.quizlet_rank
        ?? completionData.quizletRank
        ?? (timedOut ? derivedOverallRank : null);
      const rankPending = timedOut ? false : typeof mergedRank !== 'number';

      const answeredQuestions = completionData.answeredQuestions;
      const timeTaken = completionData.timeTaken;
      const maxPoints = completionData.maxPoints;
      const overallRank = completionData.rank;

      return {
        type: 'completed',
        title: 'Quizlet Completed',
        completionData: {
          ...completionData,
          totalQuestions: mergedTotalQuestions,
          pointsScored: mergedPoints,
          quizletRank: mergedRank,
          rankPending
        },
        message: `You answered ${answeredQuestions} out of ${mergedTotalQuestions} questions\nYou completed this quizlet in ${timeTaken} seconds\nYou scored a total of ${mergedPoints} points out of ${maxPoints} points available${overallRank ? `\nYou are ranked #${overallRank} on the leaderboard` : ''}\nStay tuned — the next quizlet will start shortly.`,
        iconColor: '#4CAF50'
      };
    }

    if (countdown !== null && countdown > 0) {
      return {
        type: 'countdown',
        title: 'Question Incoming!',
        message: 'Stay sharp — the next question is about to appear! 👀🧠',
        iconColor: '#9C27B0'
      };
    }

    return {
      type: 'waiting',
      title: 'Waiting for Questions...',
      message: 'Admin will push song questions when ready. Stay alert!',
      iconColor: '#9C27B0'
    };
  };

  const displayState = getDisplayState();

  return (
    <WaitingContainer>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'rgba(15, 23, 42, 0.9)',
          borderRadius: 2,
          mb: 3,
          border: '1px solid rgba(148, 163, 184, 0.35)',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2, color: '#e5e7eb' }}
          >
            <BackIcon />
          </IconButton>
          <FlashIcon sx={{ fontSize: 32, color: '#a855f7', mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              component="div"
              sx={{ fontWeight: 600, color: '#e5e7eb' }}
            >
              {session?.quiz_name || 'Flash Quiz'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              {displayState.type === 'stopped'
                ? 'Quizlet stopped by admin'
                : displayState.type === 'completed'
                  ? 'Quizlet completed'
                  : practiceMode
                    ? 'Demo mode — quizlet auto-plays'
                    : 'Waiting for questions... Stay alert!'}
            </Typography>
          </Box>
          <IconButton
            color="inherit"
            onClick={fetchLeaderboard}
            disabled={loadingLeaderboard}
            sx={{ color: '#e5e7eb' }}
          >
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg">
        {/* Big Countdown Display */}
        {countdown !== null && countdown > 0 && (
          <StyledCard sx={{ mb: 4, bgcolor: '#111827', border: '1px solid rgba(79, 70, 229, 0.5)' }}>
            <CardContent sx={{ textAlign: 'center', py: 5 }}>
              <Typography
                variant="h2"
                sx={{
                  color: '#e5e7eb',
                  fontWeight: 700,
                  fontSize: { xs: '3rem', md: '4rem' },
                  mb: 1,
                }}
              >
                {countdown}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: '#9ca3af', fontWeight: 400 }}
              >
                Get ready, the next question is about to start.
              </Typography>
            </CardContent>
          </StyledCard>
        )}

        {/* Display State Card - Prominent and Attractive */}
        <StyledCard
          sx={{
            mb: 4,
            background: displayState.type === 'completed'
              ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.12) 0%, rgba(15, 23, 42, 0.9) 100%)'
              : displayState.type === 'stopped'
                ? 'linear-gradient(135deg, rgba(248, 113, 113, 0.14) 0%, rgba(15, 23, 42, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(129, 140, 248, 0.14) 0%, rgba(15, 23, 42, 0.95) 100%)',
            border: `1px solid rgba(148, 163, 184, 0.35)`,
          }}
        >
          <CardContent
            sx={{
              textAlign: 'center',
              py: { xs: 3.5, md: 4.5 },
              px: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 2.5,
              }}
            >
              <FlashIcon sx={{
                fontSize: { xs: 60, md: 72 },
                color: displayState.iconColor,
                filter: 'none',
              }} />
            </Box>

            <Typography
              variant="h2"
              sx={{
                color: '#e5e7eb',
                fontWeight: 700,
                fontSize: { xs: '1.7rem', sm: '2rem', md: '2.3rem' },
                mb: 2.5,
              }}
            >
              {displayState.title}
            </Typography>

            {displayState.type === 'completed' && displayState.completionData ? (
              <Box sx={{ maxWidth: '900px', mx: 'auto' }}>

                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: 'rgba(37, 99, 235, 0.12)',
                      border: '1px solid rgba(59, 130, 246, 0.4)',
                      textAlign: 'center',
                      minHeight: 150,
                    }}>
                      <Typography
                        variant="h4"
                        sx={{ color: '#60a5fa', fontWeight: 700, mb: 0.5 }}
                      >
                        {displayState.completionData.totalQuestions || 0}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#9ca3af',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                        }}
                      >
                        Total Questions
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: 'rgba(34, 197, 94, 0.13)',
                      border: '1px solid rgba(34, 197, 94, 0.45)',
                      textAlign: 'center',
                      minHeight: 150,
                    }}>
                      <Typography
                        variant="h4"
                        sx={{ color: '#4ade80', fontWeight: 700, mb: 0.5 }}
                      >
                        {displayState.completionData.pointsScored || 0}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#9ca3af',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                        }}
                      >
                        Points Scored
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6b7280',
                          fontSize: '0.7rem',
                          display: 'block',
                          mt: 0.5,
                        }}
                      >
                        / {displayState.completionData.maxPoints || 0} max
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: 'rgba(250, 204, 21, 0.13)',
                      border: '1px solid rgba(250, 204, 21, 0.45)',
                      textAlign: 'center',
                      minHeight: 150,
                    }}>
                      <Typography
                        variant="h4"
                        sx={{ color: '#fde047', fontWeight: 700, mb: 0.5 }}
                      >
                        {displayState.completionData.timeTaken || 0}s
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#9ca3af',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                        }}
                      >
                        Time Taken
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: 'rgba(147, 51, 234, 0.13)',
                      border: '1px solid rgba(147, 51, 234, 0.5)',
                      textAlign: 'center',
                      minHeight: 150,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}> 
                      {displayState.completionData.rankPending ? (
                        <>
                          <CircularProgress
                            size={32}
                            sx={{ color: '#a855f7', mb: 1 }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#9ca3af',
                              fontSize: '0.85rem',
                              fontWeight: 500,
                            }}
                          >
                            Waiting for other players...
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="h4"
                            sx={{ color: '#a855f7', fontWeight: 700, mb: 0.5 }}
                          >
                            {displayState.completionData.quizletRank ? `#${displayState.completionData.quizletRank}` : '-'}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#9ca3af',
                              fontSize: '0.85rem',
                              fontWeight: 500,
                            }}
                          >
                            Quizlet Rank
                          </Typography>
                          {displayState.completionData.rank && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#6b7280',
                                fontSize: '0.7rem',
                                display: 'block',
                                mt: 0.5,
                              }}
                            >
                              Overall: #{displayState.completionData.rank}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
                <Typography
                  variant="h5"
                  sx={{ color: '#e5e7eb', fontWeight: 600, mt: 3 }}
                >
                  {practiceMode
                    ? 'Great work! Finishing up your demo quizlet…'
                    : 'Stay tuned! The next quizlet will start shortly.'}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="h5"
                sx={{
                  color: '#e5e7eb',
                  fontWeight: 500,
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                  whiteSpace: 'pre-line',
                  lineHeight: 1.8,
                  maxWidth: '800px',
                  mx: 'auto',
                }}
              >
                {displayState.message}
              </Typography>
            )}
          </CardContent>
        </StyledCard>

        {/* Status Cards */}
        <Grid container spacing={3} mb={4}>

          <Grid item xs={12} sm={4}>
            <StyledCard>
              <EqualHeightCardContent>
                <TrophyIcon sx={{ fontSize: 40, color: '#facc15' }} />
                <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>
                  Your position
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: '#e5e7eb', fontWeight: 600 }}
                >
                  {userPosition ? `#${userPosition}` : '-'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                  on the live leaderboard
                </Typography>
              </EqualHeightCardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} sm={4}>
            <StyledCard>
              <EqualHeightCardContent>
                {practiceMode ? (
                  <>
                    <BoltIcon sx={{ fontSize: 40, color: '#a855f7' }} />
                    <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>
                      Your points
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#e5e7eb', fontWeight: 700 }}>
                      {(() => {
                        const user = leaderboard.find((e) => e.participant_id === participant?.participant_id);
                        const pts = user?.current_score ?? participant?.current_score ?? 0;
                        return pts.toLocaleString();
                      })()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                      pts on the demo board
                    </Typography>
                  </>
                ) : (
                  <>
                    <PeopleIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
                    <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>
                      Players joined
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#e5e7eb', fontWeight: 600 }}>
                      {participantCount}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                      currently in this session
                    </Typography>
                  </>
                )}
              </EqualHeightCardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} sm={4}>
            <StyledCard>
              <EqualHeightCardContent>
                <MusicIcon sx={{ fontSize: 40, color: '#a855f7' }} />
                <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>
                  Mode
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: '#e5e7eb', fontWeight: 600 }}
                >
                  ⚡
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                  {practiceMode ? 'Demo mode is active' : 'Flash mode is active'}
                </Typography>
              </EqualHeightCardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Instructions */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ color: '#e5e7eb', mb: 2.5, fontWeight: 600 }}
            >
              How this round works
            </Typography>
            <List>
              <ListItem sx={{ py: 1 }}>
                <Typography variant="body1" sx={{ color: '#9ca3af' }}>
                  {practiceMode
                    ? '⚡ Answer correctly to earn base points — speed bonus up to 2× for lightning-fast replies!'
                    : '⚡ Questions appear instantly when the host sends them.'}
                </Typography>
              </ListItem>
              <ListItem sx={{ py: 1 }}>
                <Typography variant="body1" sx={{ color: '#9ca3af' }}>
                  {practiceMode
                    ? '🎯 Every quizlet adds to your demo score and shuffles the leaderboard.'
                    : '🎯 Answer quickly to maximise your score.'}
                </Typography>
              </ListItem>
              <ListItem sx={{ py: 1 }}>
                <Typography variant="body1" sx={{ color: '#9ca3af' }}>
                  🏆 The leaderboard updates in real-time as everyone answers.
                </Typography>
              </ListItem>
              <ListItem sx={{ py: 1 }}>
                <Typography variant="body1" sx={{ color: '#9ca3af' }}>
                  📱 Please stay on this page and avoid refreshing.
                </Typography>
              </ListItem>
            </List>
          </CardContent>
        </StyledCard>

        {/* Live Leaderboard */}
        {practiceMode ? (
          <PracticePointsLeaderboard
            leaderboard={leaderboard}
            title="Live demo leaderboard"
          />
        ) : (
        <LeaderboardCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#e5e7eb',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <TrophyIcon sx={{ mr: 1, color: '#facc15' }} />
                Live leaderboard
              </Typography>
              <Chip
                label={practiceMode ? 'Demo' : `${leaderboard.length} players`}
                sx={{ backgroundColor: '#a855f7', color: '#020617' }}
              />
            </Box>

            {loadingLeaderboard ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#a855f7' }} />
                <Typography sx={{ color: '#e5e7eb', ml: 2 }}>
                  Loading leaderboard...
                </Typography>
              </Box>
            ) : leaderboard.length > 0 ? (
              <List>
                {leaderboard.slice(0, 10).map((entry, index) => {
                  const isCurrentUser = entry.participant_id === participant?.participant_id;
                  const position = index + 1;
                  const displayName = (() => {
                    const guest = (entry.guest_name || '').trim();
                    if (guest) return guest;
                    const fname = (entry.fname || '').trim();
                    const lname = (entry.lname || '').trim();
                    const combined = `${fname} ${lname}`.trim();
                    return combined || (entry.anonymous_id || 'Anonymous');
                  })();

                  return (
                    <ListItem
                      key={entry.participant_id}
                      sx={{
                        py: 2,
                        backgroundColor: isCurrentUser
                          ? 'rgba(168, 85, 247, 0.08)'
                          : 'transparent',
                        borderRadius: '8px',
                        mb: 1,
                        border: isCurrentUser
                          ? '1px solid rgba(168, 85, 247, 0.7)'
                          : '1px solid transparent',
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{
                          bgcolor:
                            position === 1
                              ? '#facc15'
                              : position === 2
                                ? '#e5e7eb'
                                : position === 3
                                  ? '#fb7185'
                                  : '#5684f0',
                          width: 40,
                          height: 40,
                          fontSize: '1rem',
                          fontWeight: 'bold'
                        }}>
                          {position}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            sx={{ color: '#e5e7eb', fontWeight: 500 }}
                          >
                            {displayName}
                            {isCurrentUser && (
                              <Chip
                                label="You"
                                size="small"
                                sx={{
                                  ml: 1,
                                  backgroundColor: '#a855f7',
                                  color: '#020617',
                                  fontSize: '0.7rem'
                                }}
                              />
                            )}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                            {entry.current_score || 0} points
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
                {leaderboard.length > 10 && (
                  <ListItem sx={{ py: 2 }}>
                    <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', width: '100%' }}>
                      ... and {leaderboard.length - 10} more players
                    </Typography>
                  </ListItem>
                )}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  No players on leaderboard yet
                </Typography>
              </Box>
            )}

            {/* User's current stats */}
            {/* {userEntry && (
              <>
                <Divider sx={{ my: 2, borderColor: '#333' }} />
                <Box sx={{ p: 2, backgroundColor: '#121212', borderRadius: '8px' }}>
                  <Typography variant="h6" sx={{ color: '#9C27B0', mb: 1 }}>
                    Your Stats
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#fff' }}>
                    Position: #{userPosition} • Score: {userEntry.current_score || 0} points
                  </Typography>
                </Box>
              </>
            )} */}
          </CardContent>
        </LeaderboardCard>
        )}

        {/* Quizlet Status */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography
                variant="h6"
                sx={{ color: '#e5e7eb', fontWeight: 600 }}
              >
                Quizlet status
              </Typography>
              <Chip
                label={`${pushedQuizlets.length} quizlets`}
                sx={{ backgroundColor: '#111827', color: '#e5e7eb' }}
              />
            </Box>

            {pushedQuizlets.length ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {pushedQuizlets.map((q, idx) => {
                  const title = q.song?.name || q.song?.title || 'Unknown Song';
                  const artist = q.song?.artist || (Array.isArray(q.song?.artists) ? q.song?.artists.join(', ') : q.song?.artist_name) || 'Unknown Artist';
                  const image = q.song?.image || q.song?.cover || q.song?.thumbnail || q.song?.artwork;
                  const stats = quizletStats[q.id] || null;
                  const hasStats = !!stats;
                  const timeTakenSeconds = stats ? getQuizletTimeTakenSeconds(stats) : null;
                  const isCompleted = q.status === 'completed' || hasStats; // User has completed if they have stats
                  const isRunning = q.status === 'running' && idx === 0 && !isCompleted; // Only running if not completed

                  return (
                    <Box
                      key={q.id || idx}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid rgba(31, 41, 55, 0.9)',
                        background: isCompleted
                          ? 'linear-gradient(90deg, rgba(34,197,94,0.10), rgba(15,23,42,0.9))'
                          : isRunning
                            ? 'linear-gradient(90deg, rgba(129,140,248,0.12), rgba(15,23,42,0.9))'
                            : '#020617',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Song Info Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 2 }}>
                        <Box sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          bgcolor: '#020617',
                          border: '1px solid rgba(31,41,55,0.9)',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}>
                          {image ? (
                            <img src={image} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#777', fontSize: 12 }}>
                              No Art
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: '#e5e7eb',
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#9ca3af',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {artist}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {isCompleted ? (
                            <Chip
                              size="small"
                              label="Completed"
                              sx={{
                                bgcolor: 'rgba(34,197,94,0.12)',
                                color: '#bbf7d0',
                                border: '1px solid rgba(22,163,74,0.5)',
                              }}
                            />
                          ) : isRunning ? (
                            <Chip
                              size="small"
                              label="Running"
                              sx={{
                                bgcolor: 'rgba(129,140,248,0.15)',
                                color: '#e0e7ff',
                                border: '1px solid rgba(79,70,229,0.6)',
                              }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Expired"
                              sx={{
                                bgcolor: 'rgba(31,41,55,0.8)',
                                color: '#e5e7eb',
                                border: '1px solid rgba(55,65,81,0.9)',
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      {/* Stats Boxes - Only show if user has completed this quizlet */}
                      {hasStats && (
                        <Box sx={{ px: 2, pb: 2 }}>
                          <Divider sx={{ mb: 2, borderColor: '#111827' }} />
                          <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: 'rgba(59,130,246,0.10)',
                                border: '1px solid rgba(59,130,246,0.35)',
                                textAlign: 'center'
                              }}>
                                <Typography
                                  variant="h5"
                                  sx={{ color: '#60a5fa', fontWeight: 600, mb: 0.5 }}
                                >
                                  {stats.questions_count || 0}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#9ca3af', fontSize: '0.75rem' }}
                                >
                                  Questions
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: 'rgba(34,197,94,0.10)',
                                border: '1px solid rgba(34,197,94,0.35)',
                                textAlign: 'center'
                              }}>
                                <Typography
                                  variant="h5"
                                  sx={{ color: '#4ade80', fontWeight: 600, mb: 0.5 }}
                                >
                                  {stats.total_points || 0}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#9ca3af', fontSize: '0.75rem' }}
                                >
                                  Total Points
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: 'rgba(250, 204, 21, 0.10)',
                                border: '1px solid rgba(250, 204, 21, 0.35)',
                                textAlign: 'center'
                              }}>
                                <Typography
                                  variant="h5"
                                  sx={{ color: '#fde047', fontWeight: 600, mb: 0.5 }}
                                >
                                  {stats.quizlet_rank ? (
                                    `#${stats.quizlet_rank}`
                                  ) : (
                                    <CircularProgress
                                      size={24}
                                      sx={{ color: '#fde047', mt: 0.5 }}
                                    />
                                  )}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#9ca3af', fontSize: '0.75rem' }}
                                >
                                  Quizlet Rank
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: 'rgba(129, 140, 248, 0.10)',
                                border: '1px solid rgba(129, 140, 248, 0.35)',
                                textAlign: 'center'
                              }}>
                                <Typography
                                  variant="h5"
                                  sx={{ color: '#a855f7', fontWeight: 600, mb: 0.5 }}
                                >
                                  {typeof timeTakenSeconds === 'number' ? `${timeTakenSeconds}s` : 'N/A'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#9ca3af', fontSize: '0.75rem' }}
                                >
                                  Time Taken
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  No quizlets pushed yet
                </Typography>
              </Box>
            )}
          </CardContent>
        </StyledCard>

        {/* Footer */}
        <Box textAlign="center" mt={4}>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            {practiceMode
              ? 'Demo quizlet plays automatically — no host needed ⚡'
              : 'Waiting for the host to push questions… ⚡'}
          </Typography>
        </Box>
      </Container>
    </WaitingContainer>
  );
};

export default FlashQuizWaiting;